import { motion } from "framer-motion";
import Corners from "./Corners";

const TIERS = [
  {
    id: "bundle",
    name: "MLO ACCELERATOR BUNDLE",
    price: "$2,997",
    priceSub: "one-time",
    desc: "The complete done-for-you system.",
    features: [
      "DFY Webinar Funnel",
      "DFY Ad Copy & Creatives",
      "GHL CRM Snapshot",
      "Webinar Script",
      "4× Coaching Calls",
      "Permanent Registry List",
      "90-Day Nurture Sequence",
    ],
    cta: "GET STARTED",
    ctaStyle: "ghost",
    featured: false,
  },
  {
    id: "sprint",
    name: "BUNDLE + SPRINT",
    price: "$3,497",
    priceSub: "one-time · $2,997 + $500",
    desc: "Everything in the Bundle, plus 14 days of active ad management.",
    features: [
      "Everything in the Bundle",
      "⚡ 14-Day Managed Ad Campaign",
      "Daily Optimization",
      "Priority Slack Channel",
      "Hands-on Launch Support",
    ],
    guarantee: "First pre-qualified applicant — or we keep working at no charge.",
    cta: "APPLY FOR SPRINT",
    ctaStyle: "primary",
    featured: true,
  },
  {
    id: "retainer",
    name: "RETAINER",
    price: "$2,497",
    priceSub: "/ month",
    desc: "Ongoing full-service Meta ad management after your funnel is live.",
    features: [
      "Weekly Optimization",
      "Monthly Reporting",
      "Creative Refresh",
      "Priority Support",
    ],
    note: "Ad spend billed separately. 12-month agreement.",
    cta: "LEARN MORE",
    ctaStyle: "ghost",
    featured: false,
  },
];

export default function Pricing() {
  const scrollToApply = () => {
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="pricing"
      className="relative py-32 md:py-48"
      data-testid="pricing-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#F0ECE4]/40">
            [006] —
          </span>
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#C9A84C]">
            INVESTMENT
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display max-w-[1100px] mb-20"
          style={{ fontSize: "clamp(34px, 5vw, 80px)", lineHeight: 0.95, letterSpacing: "-0.035em" }}
        >
          One system. <span className="text-[#C9A84C]">Three tiers.</span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((tier, i) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -8 }}
              className={`relative p-8 md:p-10 flex flex-col ${
                tier.featured
                  ? "border border-[#C9A84C] bg-[#C9A84C]/[0.04] md:scale-[1.03]"
                  : "border border-white/5 bg-white/[0.02]"
              }`}
              data-testid={`pricing-tier-${tier.id}`}
            >
              <Corners color={tier.featured ? "#C9A84C" : "#C9A84C"} />
              {tier.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A84C] px-4 py-1 font-mono text-[10px] font-bold tracking-[0.28em] text-[#070709]">
                  MOST POPULAR
                </div>
              )}

              <div className="font-mono text-[10px] tracking-[0.28em] text-[#F0ECE4]/50 mb-4">
                TIER {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-display text-[20px] md:text-[24px] tracking-[-0.02em] leading-[1.1]">
                {tier.name}
              </div>
              <p className="mt-3 font-body text-[13px] leading-[1.6] text-[#F0ECE4]/55">
                {tier.desc}
              </p>

              <div className="mt-8 flex items-baseline gap-2">
                <div
                  className="font-display text-[#F0ECE4]"
                  style={{ fontSize: "clamp(44px, 4.5vw, 68px)", lineHeight: 1, letterSpacing: "-0.04em" }}
                >
                  {tier.price}
                </div>
                <div className="font-mono text-[10px] tracking-[0.22em] text-[#F0ECE4]/50">
                  {tier.priceSub}
                </div>
              </div>

              <ul className="mt-8 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 font-body text-[13px] text-[#F0ECE4]/80">
                    <span className="text-[#C9A84C] mt-[3px]">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {tier.guarantee && (
                <div className="mt-6 bg-[#C9A84C] px-4 py-3 font-mono text-[10px] font-bold tracking-[0.14em] text-[#070709] leading-[1.5]">
                  THE GUARANTEE: {tier.guarantee}
                </div>
              )}

              {tier.note && (
                <div className="mt-6 font-mono text-[9px] tracking-[0.2em] text-[#F0ECE4]/40 leading-[1.6]">
                  {tier.note}
                </div>
              )}

              <button
                onClick={scrollToApply}
                data-testid={`pricing-cta-${tier.id}`}
                className={
                  (tier.ctaStyle === "primary" ? "wam-btn-primary" : "wam-btn-ghost") +
                  " mt-8 w-full text-[12px]"
                }
              >
                {tier.cta} →
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
